'use client'

import { verifyImage, sendFeedback, FeedbackRequest } from '@/services/playground.api';
import React, { useState } from 'react';
import styles from "./page.module.css";
import ImageUpload from '@/components/upload/index.client';
import {
  Button,
  Divider,
  Progress,
  Input,
  Form,
  type FormProps,
} from "antd";

import FormItem from 'antd/es/form/FormItem';
import { RuleObject } from 'antd/lib/form';
import { ProgressProps } from 'antd/lib/progress';
import { isImageUrl } from '@/utils/isImageUrl';
import Result, { Feedback, ResultType } from '@/components/result/index.client';
import Footer from "@/components/Footer";

type FieldType = {
  imageFile: string;
  imageUrl: string;
}

type ValidateInputParams = {
  getFieldValue: (field: string) => string;
}


const PlayGround = () => {
  const [form] = Form.useForm();
  const [image, setImage] = useState<string>('');
  const [result, setResult] = useState<ResultType>();
  const [feedback, setFeedback] = useState<Feedback>();
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingStatus, setLoadingStatus] = useState<ProgressProps['status']>('normal');
  const [loadingPercent, setLoadingPercent] = useState<number>(0);

  const onUploadChange = async (file: File) => {
    console.log(file);
    const reader = new FileReader();
    reader.addEventListener('load', async () => {
      const base64 = reader.result as string;
      setImage(base64)
      form.setFieldsValue({ imageUrl: '' });
    });
    reader.readAsDataURL(file);
  }

  const onInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const isValid = isImageUrl(e.target.value);
    if (isValid) {
      setImage(e.target.value)
      form.setFieldsValue({ imageFile: '' });
    } else {
      setImage('')
    }
  }

  const updateProgress = () => {
    setTimeout(() => {
      const random = Math.floor(Math.random() * 20);
      setLoadingPercent((value) => value + random);
      if (loadingPercent < 95) {
        updateProgress();
      }
    }, 1000);
  }

  const requestApi = async (values: FieldType) => {
    setFeedback(undefined);
    const response = await verifyImage(values.imageFile || values.imageUrl);
    return response.data;
  };

  const requestFeedback = async (data: FeedbackRequest) => {
    if (feedback) {
      return;
    }
    setFeedback(data.feedback);
    const response = await sendFeedback(data);
    return response.data;
  };

  const validateImageUrl = ({ getFieldValue }: ValidateInputParams) => ({
    validator(_: RuleObject, value: string) {
      const imageFile = getFieldValue("imageFile");
      const imageUrl = getFieldValue("imageUrl");

      if (imageFile) {
        return Promise.resolve();
      }

      if (imageUrl && !isImageUrl(imageUrl)) {
        return Promise.reject(
          new Error(
            "Please enter a valid image URL ending with .jpeg, .jpg, .png, .gif, or .bmp.",
          ),
        );
      }

      if (!imageFile && !imageUrl) {
        return Promise.reject(
          new Error("Please upload an image or enter an image URL."),
        );
      }

      return Promise.resolve();
    },
  });
    
  const onVerify: FormProps<FieldType>["onFinish"] = async (values) => {
    console.log(values);
    setLoading(true);
    setResult(undefined);
    setLoadingPercent(1);
    setLoadingStatus('normal');
    updateProgress();
    try {
      const resData = await requestApi(values)
      console.log(resData);
      setResult({
        confidence: resData.confidence,
        valid: resData.is_driving_license,
        id: resData.id,
      });
    } catch (error) {
      console.error(error);
      setLoadingStatus('exception');
    }
    setLoadingPercent(100);
    setLoading(false);
  }

  return (
    <>
      <div className={styles.app}>
        <Form
          style={{ width: '100%' }}
          form={form}
          onFinish={onVerify}
        >
          <FormItem
            name="imageFile"
          >
            <ImageUpload onChange={onUploadChange} />
          </FormItem>
          <div>
            <Divider plain>Or</Divider>
          </div>
          <FormItem
            name="imageUrl"
            dependencies={['imageFile']}
            rules={[{ required: false, message: '' }, validateImageUrl]}
          >
            <Input
              size="large"
              placeholder="Input Image url"
              onChange={onInputChange}
            />
          </FormItem>
          <div className={styles['btn-wrap']}>
            <Button
              block
              loading={loading}
              type="primary"
              htmlType="submit"
            >
              Verify
            </Button>
          </div>
        </Form>
        {loadingPercent > 0 && <Progress percent={loadingPercent} status={loadingStatus} />}
        <Result result={result} feedback={feedback} onFeedback={(feedback)=> requestFeedback({ id: result!.id, feedback })}/>
        <div className={styles['image-wrap']}>
          {image && <img src={image} alt="image" />}
        </div>
      </div>
      <Footer />
    </>
  );
};


export default PlayGround;
